import { useState } from "react";
import { toast } from "sonner";
import { submitEnquiry } from "@/lib/api";
import { BRAND, whatsappLink } from "@/lib/brand";
import { MessageCircle, Phone } from "lucide-react";

const emptyByType = {
  stay:      { name: "", mobile: "", email: "", check_in: "", check_out: "", guests: "2", room_type: "Premium Couple" },
  chardham:  { name: "", mobile: "", email: "", travel_month: "", guests: "2", package: "Kedarnath Yatra" },
  wedding:   { name: "", mobile: "", email: "", wedding_month: "", expected_guests: "50", city: "" },
  contact:   { name: "", mobile: "", email: "", message: "" },
};

const submitLabel = {
  stay: "Book Your Stay",
  chardham: "Book Your Yatra Package",
  wedding: "Book Your Triyuginarayan Wedding",
  contact: "Send Message",
};

const successMsg = {
  stay: "Your stay enquiry has been received. We look forward to welcoming you to the Himalayas.",
  chardham: "Thank you. Your Himalayan journey has begun. Our travel expert will contact you shortly.",
  wedding: "Thank you. Let us begin curating your Himalayan wedding.",
  contact: "Thank you for reaching out. Our team will connect with you soon.",
};

const heading = {
  stay: "Reserve Your Stay",
  chardham: "Plan Your Sacred Journey",
  wedding: "Celebrate Your Dream Wedding",
  contact: "Write to us",
};

export const EnquiryForm = ({ type = "stay", dark = false, initial = null }) => {
  const [form, setForm] = useState({ ...emptyByType[type], ...(initial || {}) });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) { toast.error("Please share your name and mobile number."); return; }
    setBusy(true);
    try {
      await submitEnquiry({ type, ...form });
      setDone(true);
      toast.success("Received. Namaste 🙏");
    } catch {
      toast.error("Something interrupted our line. Please WhatsApp us instead.");
    } finally { setBusy(false); }
  };

  const wrapCls = dark
    ? "p-8 md:p-12 bg-[#0F0F0F] text-[#F8F6F2] border border-[#D4AF37]/25"
    : "p-8 md:p-12 bg-surface border border-kc";

  if (done) {
    const contextMsg = {
      stay:     `Namaste 🙏 My stay enquiry is submitted. Guest name: ${form.name}, mobile: ${form.mobile}. Please share room availability and confirm booking.`,
      chardham: `Namaste 🙏 My yatra enquiry is submitted. Guest name: ${form.name}, mobile: ${form.mobile}. Please share the package details and dates.`,
      wedding:  `Namaste 🙏 My wedding enquiry is submitted. Family name: ${form.name}, mobile: ${form.mobile}. Please share detailed packages.`,
      contact:  `Namaste 🙏 I've just submitted an enquiry. Name: ${form.name}, mobile: ${form.mobile}. Continuing the conversation here.`,
    }[type] || `Namaste 🙏 My enquiry is submitted. Please continue on WhatsApp.`;
    return (
      <div className={wrapCls} data-testid={`enquiry-success-${type}`}>
        <div className="overline no-line">Namaste</div>
        <h3 className="font-serif-display text-3xl md:text-4xl mt-4 leading-tight">{successMsg[type]}</h3>
        <p className={`mt-6 text-[14px] leading-relaxed ${dark ? "text-[#F8F6F2]/70" : "text-ink-soft"}`}>
          A member of our team will personally reach you on {form.mobile}.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href={whatsappLink(contextMsg)} target="_blank" rel="noreferrer" className="btn-gold inline-flex" data-testid={`enq-${type}-continue-wa`}>
            <MessageCircle size={14} /> Continue on WhatsApp
          </a>
          <a href={`tel:${BRAND.phone.replace(/\s/g,"")}`} className={dark ? "btn-ghost-light inline-flex" : "btn-ghost inline-flex"} data-testid={`enq-${type}-call-now`}>
            <Phone size={13} /> Call Now
          </a>
        </div>
      </div>
    );
  }

  const field = dark
    ? "w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3.5 text-[13.5px] text-[#F8F6F2] placeholder:text-[#F8F6F2]/40 focus:outline-none focus:border-[#D4AF37] transition-colors tracking-wide"
    : "w-full bg-transparent border border-kc px-4 py-3.5 text-[13.5px] text-ink placeholder:text-ink-mute focus:outline-none focus:border-gold transition-colors tracking-wide";

  const label = dark ? "text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-2 block" : "text-[10px] uppercase tracking-[0.28em] text-gold mb-2 block";

  return (
    <form onSubmit={submit} className={wrapCls} data-testid={`enquiry-form-${type}`}>
      <div className="overline no-line">Begin Your Journey</div>
      <h3 className="font-serif-display text-3xl md:text-4xl mt-4 mb-10 leading-tight">
        {heading[type]}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <span className={label}>Name</span>
          <input className={field} placeholder="Your full name" value={form.name} onChange={e => upd("name", e.target.value)} data-testid={`enq-${type}-name`} required />
        </div>
        <div>
          <span className={label}>Mobile</span>
          <input className={field} placeholder="+91 …" value={form.mobile} onChange={e => upd("mobile", e.target.value)} data-testid={`enq-${type}-mobile`} required />
        </div>
        <div className="md:col-span-2">
          <span className={label}>Email · Optional</span>
          <input className={field} placeholder="you@example.com" value={form.email || ""} onChange={e => upd("email", e.target.value)} data-testid={`enq-${type}-email`} />
        </div>

        {type === "stay" && (<>
          <div>
            <span className={label}>Check-in</span>
            <input type="date" className={field} value={form.check_in || ""} onChange={e => upd("check_in", e.target.value)} data-testid="enq-stay-checkin" />
          </div>
          <div>
            <span className={label}>Check-out</span>
            <input type="date" className={field} value={form.check_out || ""} onChange={e => upd("check_out", e.target.value)} data-testid="enq-stay-checkout" />
          </div>
          <div>
            <span className={label}>Guests</span>
            <input className={field} placeholder="e.g. 2" value={form.guests || ""} onChange={e => upd("guests", e.target.value)} data-testid="enq-stay-guests" />
          </div>
          <div>
            <span className={label}>Room</span>
            <select className={field} value={form.room_type || ""} onChange={e => upd("room_type", e.target.value)} data-testid="enq-stay-room">
              <option>Premium Couple</option><option>Premium Family</option><option>Premium Family Suite</option>
            </select>
          </div>
        </>)}

        {type === "chardham" && (<>
          <div>
            <span className={label}>Travel Month</span>
            <input className={field} placeholder="e.g. May 2026" value={form.travel_month || ""} onChange={e => upd("travel_month", e.target.value)} data-testid="enq-chardham-month" />
          </div>
          <div>
            <span className={label}>Guests</span>
            <input className={field} placeholder="e.g. 4" value={form.guests || ""} onChange={e => upd("guests", e.target.value)} data-testid="enq-chardham-guests" />
          </div>
          <div className="md:col-span-2">
            <span className={label}>Preferred Package</span>
            <select className={field} value={form.package || ""} onChange={e => upd("package", e.target.value)} data-testid="enq-chardham-package">
              <option>Kedarnath Yatra</option><option>Do Dham (Kedarnath + Badrinath)</option><option>Char Dham Yatra</option><option>Helicopter Assistance</option>
            </select>
          </div>
        </>)}

        {type === "wedding" && (<>
          <div>
            <span className={label}>Wedding Month</span>
            <input className={field} placeholder="e.g. October 2026" value={form.wedding_month || ""} onChange={e => upd("wedding_month", e.target.value)} data-testid="enq-wedding-month" />
          </div>
          <div>
            <span className={label}>Expected Guests</span>
            <input className={field} placeholder="e.g. 60" value={form.expected_guests || ""} onChange={e => upd("expected_guests", e.target.value)} data-testid="enq-wedding-guests" />
          </div>
          <div className="md:col-span-2">
            <span className={label}>City</span>
            <input className={field} placeholder="Your city" value={form.city || ""} onChange={e => upd("city", e.target.value)} data-testid="enq-wedding-city" />
          </div>
        </>)}

        {type === "contact" && (
          <div className="md:col-span-2">
            <span className={label}>Message</span>
            <textarea rows={4} className={field} placeholder="How may we serve you?" value={form.message || ""} onChange={e => upd("message", e.target.value)} data-testid="enq-contact-message" />
          </div>
        )}
      </div>

      <button type="submit" disabled={busy} className={`mt-10 ${dark ? "btn-gold" : "btn-saffron"} w-full md:w-auto justify-center`} data-testid={`enq-${type}-submit`}>
        {busy ? "Sending…" : submitLabel[type]}
      </button>
      <p className={`mt-5 text-[10px] uppercase tracking-[0.24em] ${dark ? "text-[#F8F6F2]/40" : "text-ink-mute"}`}>By sharing your details, you consent to be contacted by THE KC INDIA.</p>
    </form>
  );
};
